<template>
  <div class="back-calculator">
    <h1>經驗回推計算機</h1>
    
    <div class="calculator-container">
      <!-- 輸入區域 -->
      <div class="input-section">
        <!-- 當前等級 -->
        <div class="input-group">
          <h2>當前等級</h2>
          <div class="level-input-container">
            <div class="level-input">
              <input 
                type="number" 
                v-model.number="currentLevel" 
                min="200"
                max="300"
                placeholder="請輸入當前等級"
                @input="validateInput('currentLevel')"
                @blur="validateLevelOnBlur"
                step="1"
              >
              <div class="error-message" v-if="errors.currentLevel">
                最低使用等級200
              </div>
            </div>
            <div class="percentage-input">
              <input 
                type="number" 
                v-model.number="currentExpPercentageInput" 
                min="0"
                max="100"
                step="0.001"
                placeholder="經驗值%"
                @input="updateExpFromPercentage"
              >
              <span class="percentage-symbol">%</span>
            </div>
          </div>
        </div>

        <!-- 當前經驗值進度 -->
        <div class="input-group">
          <h2>當前經驗值進度</h2>
          <div class="exp-progress-container">
            <div class="exp-progress-bar">
              <div class="exp-progress-fill" :style="{ width: `${currentExpPercentage}%` }"></div>
            </div>
            <div class="exp-progress-info">
              <div class="exp-percentage">{{ currentExpPercentage.toFixed(3) }}%</div>
              <div class="exp-values">
                <input 
                  type="text" 
                  v-model="currentExpValue" 
                  @input="updateExpFromValue"
                  class="exp-value-input"
                >
                /
                <span class="total-exp">{{ formattedLevelExp }}</span>
              </div>
            </div>
            <div class="burning-buttons">
              <div class="burning-left">
                <!-- 燃燒功能暫時關閉 -->
                <!-- <div v-if="currentLevel < 260" class="burning-controls">
                  <button 
                    @click="setBurningMode('burning1+2')" 
                    class="burning-button"
                    :class="{ active: burningMode === 'burning1+2' }"
                  >燃燒1+2</button>
                  <button 
                    @click="setBurningMode('burning1+4')" 
                    class="burning-button"
                    :class="{ active: burningMode === 'burning1+4' }"
                  >燃燒1+4</button>
                </div>
                <div v-if="currentLevel < 260" class="burning-notice">
                  ※請確認燃燒角色是否能使用經驗秘藥
                </div> -->
              </div>
              <div class="burning-right">
                <button @click="resetCurrentLevel" class="reset-level-button">重設等級</button>
              </div>
            </div>
          </div>
        </div>

        <!-- 目標等級 -->
        <div class="input-group">
          <h2>目標等級</h2>
          <div class="level-input-container">
            <div class="level-input">
              <input 
                type="number" 
                v-model.number="targetLevel" 
                min="200"
                max="300"
                placeholder="請輸入目標等級"
                @input="validateInput('targetLevel')"
                @blur="validateTargetLevelOnBlur"
                step="1"
              >
              <div class="error-message" v-if="errors.targetLevel">
                最低使用等級200
              </div>
            </div>
            <div class="percentage-input">
              <input 
                type="number" 
                v-model.number="targetExpPercentageInput" 
                min="0"
                max="100"
                step="0.001"
                placeholder="經驗值%"
                @input="updateTargetExpFromPercentage"
              >
              <span class="percentage-symbol">%</span>
            </div>
          </div>
        </div>

        <!-- 目標經驗值進度 -->
        <div class="input-group">
          <h2>目標經驗值進度</h2>
          <div class="exp-progress-container">
            <div class="exp-progress-bar">
              <div class="exp-progress-fill" :style="{ width: `${targetExpPercentage}%` }"></div>
            </div>
            <div class="exp-progress-info">
              <div class="exp-percentage">{{ targetExpPercentage.toFixed(3) }}%</div>
              <div class="exp-values">
                <input 
                  type="text" 
                  v-model="targetExpValue" 
                  @input="updateTargetExpFromValue"
                  class="exp-value-input"
                >
                /
                <span class="total-exp">{{ formattedTargetLevelExp }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 按鈕區域 -->
        <div class="button-group">
          <button 
            @click="calculateRequiredItems" 
            class="calculate-button"
            :disabled="!canCalculate"
            :class="{ 'disabled': !canCalculate }"
          >確認</button>
          <button 
            @click="resetTargetLevel" 
            class="reset-button"
            :disabled="!targetLevel"
            :class="{ 'disabled': !targetLevel }"
          >重設</button>
        </div>

        <!-- 結果區域 -->
        <div v-if="showResult" class="result-section">
          <h2>所需物品</h2>
          <div class="result-content">
            <div class="required-items">
              <h3>經驗秘藥</h3>
              <div class="item-list">
                <div class="item-item" v-for="(item, index) in requiredPotions" :key="index">
                  <div class="item-info">
                    <img 
                      :src="item.maxLevel === 200 ? require('@/assets/water/200.png') : require(`@/assets/water/${item.maxLevel}.png`)" 
                      :alt="item.name"
                      class="item-image"
                    >
                    <span class="item-name">{{ item.name }}</span>
                  </div>
                  <div class="item-count">{{ item.count }} 個</div>
                </div>
              </div>
              
              <h3>EXP 交換券</h3>
              <div class="item-list">
                <div class="item-item">
                  <div class="item-info">
                    <img 
                      :src="require('@/assets/exp.png')" 
                      alt="EXP 交換券"
                      class="item-image"
                    >
                    <span class="item-name">EXP 交換券</span>
                  </div>
                  <div class="item-count">{{ requiredTickets.expTicket }} 張</div>
                </div>
                <div class="item-item" v-if="currentLevel >= 260">
                  <div class="item-info">
                    <img 
                      :src="require('@/assets/expplus.png')" 
                      alt="上級 EXP 交換券"
                      class="item-image"
                    >
                    <span class="item-name">上級 EXP 交換券</span>
                  </div>
                  <div class="item-count">{{ requiredTickets.expPlusTicket }} 張</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { potions } from '@/data/expData'
import { ExpCalculator } from '@/utils/ExpCalculator'

export default {
  name: 'BackCalc',
  data() {
    return {
      calculator: new ExpCalculator(),
      currentLevel: null,
      currentExpValue: '0',
      currentExpPercentageInput: null,
      targetLevel: null,
      targetExpValue: '0',
      targetExpPercentageInput: 0,
      errors: {
        currentLevel: false,
        targetLevel: false
      },
      showResult: false,
      burningMode: null,
      requiredPotions: [],
      requiredTickets: {
        expTicket: 0,
        expPlusTicket: 0
      }
    }
  },
  created() {
    this.loadFromLocalStorage()
  },
  computed: {
    currentLevelExp() {
      return this.calculator.getLevelExp(this.currentLevel)
    },
    formattedLevelExp() {
      return this.calculator.formatNumber(this.currentLevelExp)
    },
    targetLevelExp() {
      return this.calculator.getLevelExp(this.targetLevel)
    },
    formattedTargetLevelExp() {
      return this.calculator.formatNumber(this.targetLevelExp)
    },
    currentExpPercentage() {
      return this.calculator.calculateExpPercentage(this.currentLevel, this.currentExpValue)
    },
    targetExpPercentage() {
      return this.calculator.calculateExpPercentage(this.targetLevel, this.targetExpValue)
    },
    canCalculate() {
      const currentExpValid = this.currentExpValue && this.currentExpValue !== ''
      const targetExpValid = this.targetExpValue && this.targetExpValue !== ''
      
      return this.currentLevel && this.targetLevel && 
             currentExpValid && targetExpValid &&
             !this.errors.currentLevel && !this.errors.targetLevel
    }
  },
  watch: {
    currentLevel() {
      this.saveToLocalStorage()
      if (this.currentLevel >= 260) {
        this.burningMode = null
      }
    },
    currentExpValue() {
      this.saveToLocalStorage()
    },
    currentExpPercentageInput() {
      this.saveToLocalStorage()
    },
    targetLevel() {
      this.saveToLocalStorage()
    },
    targetExpValue() {
      this.saveToLocalStorage()
    },
    targetExpPercentageInput() {
      this.saveToLocalStorage()
    }
  },
  methods: {
    validateInput(field) {
      const value = this[field]
      if (value === null || value === '') {
        this.errors[field] = false
        return
      }

      if (field === 'currentLevel') {
        this.currentLevel = this.calculator.sanitizeLevelInput(this.currentLevel)
        this.currentExpPercentageInput = 0
        this.currentExpValue = '0'
        this.errors[field] = false
      } else if (field === 'targetLevel') {
        this.targetLevel = this.calculator.sanitizeLevelInput(this.targetLevel)
        this.targetExpPercentageInput = 0
        this.targetExpValue = '0'
        this.errors[field] = false
      }
    },
    validateLevelOnBlur() {
      if (this.currentLevel !== null && this.currentLevel !== '') {
        this.errors.currentLevel = !this.calculator.validateLevel(this.currentLevel)
        if (this.errors.currentLevel) {
          this.currentLevel = null
        }
      }
    },
    validateTargetLevelOnBlur() {
      if (this.targetLevel !== null && this.targetLevel !== '') {
        this.errors.targetLevel = !this.calculator.validateLevel(this.targetLevel)
        if (this.errors.targetLevel) {
          this.targetLevel = null
        }
      }
    },
    updateExpFromValue() {
      if (!this.currentLevel) return
      
      const inputValue = String(this.currentExpValue || '')
      let cleanExp = inputValue.replace(/[^\d,]/g, '').replace(/,/g, '')
      if (!cleanExp) {
        this.currentExpValue = '0'
        this.currentExpPercentageInput = 0
        return
      }

      const result = this.calculator.validateAndFormatExp(this.currentLevel, cleanExp)
      this.currentExpValue = result.formattedExp
      this.currentExpPercentageInput = parseFloat(result.percentage.toFixed(3))
    },
    updateExpFromPercentage() {
      if (!this.currentLevel || this.currentExpPercentageInput === null) return
      
      let percentage = parseFloat(this.currentExpPercentageInput)
      if (isNaN(percentage)) {
        percentage = 0
      }
      
      let validPercentage = Math.min(Math.max(percentage, 0), 100)
      
      const result = this.calculator.validateAndFormatPercentage(this.currentLevel, validPercentage)
      this.currentExpValue = result.formattedExp
      this.currentExpPercentageInput = parseFloat(result.percentage.toFixed(3))
    },
    updateTargetExpFromValue() {
      if (!this.targetLevel) return
      
      const inputValue = String(this.targetExpValue || '')
      let cleanExp = inputValue.replace(/[^\d,]/g, '').replace(/,/g, '')
      if (!cleanExp) {
        this.targetExpValue = '0'
        this.targetExpPercentageInput = 0
        return
      }

      const result = this.calculator.validateAndFormatExp(this.targetLevel, cleanExp)
      this.targetExpValue = result.formattedExp
      this.targetExpPercentageInput = parseFloat(result.percentage.toFixed(3))
    },
    updateTargetExpFromPercentage() {
      if (!this.targetLevel || this.targetExpPercentageInput === null) return
      
      let percentage = parseFloat(this.targetExpPercentageInput)
      if (isNaN(percentage)) {
        percentage = 0
      }
      
      let validPercentage = Math.min(Math.max(percentage, 0), 100)
      
      const result = this.calculator.validateAndFormatPercentage(this.targetLevel, validPercentage)
      this.targetExpValue = result.formattedExp
      this.targetExpPercentageInput = parseFloat(result.percentage.toFixed(3))
    },
    setBurningMode(mode) {
      if (this.currentLevel >= 260) {
        this.burningMode = null
        return
      }
      
      if (this.burningMode === mode) {
        this.burningMode = null
      } else {
        this.burningMode = mode
      }
      this.showResult = false
    },
    calculateRequiredItems() {
      if (!this.canCalculate) return

      // 計算需要的經驗值
      const currentExp = BigInt(String(this.currentExpValue).replace(/,/g, ''))
      const targetExp = BigInt(String(this.targetExpValue).replace(/,/g, ''))
      
      // 計算從當前等級到目標等級需要的總經驗值
      let totalExpNeeded = BigInt(0)
      let level = this.currentLevel
      
      // 先扣除當前等級的剩餘經驗值
      const currentLevelExp = BigInt(this.currentLevelExp)
      const currentExpInLevel = currentLevelExp - currentExp
      totalExpNeeded += currentExpInLevel
      level++
      
      // 計算到目標等級前所有等級的經驗值
      while (level < this.targetLevel) {
        totalExpNeeded += BigInt(this.calculator.getLevelExp(level))
        level++
      }
      
      // 加上目標等級的經驗值
      totalExpNeeded += targetExp
      
      // 計算需要的秘藥和票券
      this.calculateRequiredPotions(totalExpNeeded)
      this.calculateRequiredTickets(totalExpNeeded)
      
      this.showResult = true
      
      this.$nextTick(() => {
        const resultSection = document.querySelector('.result-section')
        if (resultSection) {
          resultSection.scrollIntoView({ behavior: 'smooth' })
        }
      })
    },
    calculateRequiredPotions(totalExpNeeded) {
      this.requiredPotions = []
      
      // 複製秘藥陣列並計算需要的數量
      const potionsCopy = [...potions]
      
      for (let potion of potionsCopy) {
        let expPerPotion
        if (potion.name === '究極聯盟成長秘藥' && potion.expSum) {
          expPerPotion = BigInt(potion.expSum)
        } else {
          expPerPotion = BigInt(this.calculator.getLevelExp(potion.maxLevel))
        }
        
        // 計算需要的數量，使用向上取整確保能夠觸發升級
        const count = totalExpNeeded / expPerPotion + (totalExpNeeded % expPerPotion > 0n ? 1n : 0n)
        
        if (count > 0n) {
          this.requiredPotions.push({
            name: potion.name,
            count: Number(count),
            maxLevel: potion.maxLevel
          })
        }
      }
    },
    calculateRequiredTickets(totalExpNeeded) {
      this.requiredTickets = {
        expTicket: 0,
        expPlusTicket: 0
      }
      
      // 計算需要的 EXP 交換券
      // 使用當前等級的票券數據，如果當前等級不可用則使用最低可用等級
      let expTicketLevel = this.currentLevel
      while (expTicketLevel <= 299) {
        const expTicketData = this.calculator.getTicketInfo(expTicketLevel, 'expTicket')
        if (expTicketData) {
          const expPerTicket = BigInt(expTicketData.expPerTicket)
          // 使用向上取整確保能夠觸發升級
          const ticketsNeeded = totalExpNeeded / expPerTicket + (totalExpNeeded % expPerTicket > 0n ? 1n : 0n)
          this.requiredTickets.expTicket = Number(ticketsNeeded)
          break
        }
        expTicketLevel++
      }
      
      // 計算需要的上級 EXP 交換券（只有當前等級達到260時才計算）
      if (this.currentLevel >= 260) {
        let expPlusTicketLevel = Math.max(this.currentLevel, 260)
        while (expPlusTicketLevel <= 299) {
          const expPlusTicketData = this.calculator.getTicketInfo(expPlusTicketLevel, 'expPlusTicket')
          if (expPlusTicketData) {
            const expPerTicket = BigInt(expPlusTicketData.expPerTicket)
            // 使用向上取整確保能夠觸發升級
            const ticketsNeeded = totalExpNeeded / expPerTicket + (totalExpNeeded % expPerTicket > 0n ? 1n : 0n)
            this.requiredTickets.expPlusTicket = Number(ticketsNeeded)
            break
          }
          expPlusTicketLevel++
        }
      }
    },
    resetCurrentLevel() {
      this.currentLevel = null
      this.currentExpValue = '0'
      this.currentExpPercentageInput = 0
      this.burningMode = null
      this.showResult = false
    },
    resetTargetLevel() {
      this.targetLevel = null
      this.targetExpValue = '0'
      this.targetExpPercentageInput = 0
      this.showResult = false
    },
    saveToLocalStorage() {
      const data = {
        currentLevel: this.currentLevel,
        currentExpValue: this.currentExpValue,
        currentExpPercentageInput: this.currentExpPercentageInput,
        targetLevel: this.targetLevel,
        targetExpValue: this.targetExpValue,
        targetExpPercentageInput: this.targetExpPercentageInput
      }
      localStorage.setItem('backCalcData', JSON.stringify(data))
    },
    loadFromLocalStorage() {
      const savedData = localStorage.getItem('backCalcData')
      if (savedData) {
        const data = JSON.parse(savedData)
        this.currentLevel = data.currentLevel
        this.currentExpValue = data.currentExpValue || '0'
        this.currentExpPercentageInput = data.currentExpPercentageInput || 0
        this.targetLevel = data.targetLevel
        this.targetExpValue = data.targetExpValue || '0'
        this.targetExpPercentageInput = data.targetExpPercentageInput || 0
      }
    }
  }
}
</script>

<style scoped>
.back-calculator {
  max-width: 800px;
  margin: 0 auto;
}

.back-calculator h1 {
  text-align: left;
  margin-bottom: 2rem;
  color: var(--secondary-color);
}

.calculator-container {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.input-section {
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: var(--card-shadow);
}

.input-group {
  margin-bottom: 1.5rem;
}

.input-group h2 {
  font-size: 1.2rem;
  color: var(--secondary-color);
  margin-bottom: 0.5rem;
}

.input-group input {
  width: 100%;
  padding: 0.8rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
}

.input-group input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2);
}

.level-input-container {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
}

.level-input,
.percentage-input {
  flex: 1;
  position: relative;
}

.percentage-input input {
  padding-right: 2rem !important;
}

.percentage-symbol {
  position: absolute;
  right: 0.8rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-color);
  pointer-events: none;
}

.exp-progress-container {
  margin-top: 0.5rem;
  display: flex;
  flex-direction: column;
}

.exp-progress-bar {
  width: 100%;
  height: 20px;
  background-color: #f0f0f0;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 0.5rem;
}

.exp-progress-fill {
  height: 100%;
  background-color: var(--primary-color);
  transition: width 0.3s ease;
}

.exp-progress-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.9rem;
}

.exp-percentage {
  color: var(--primary-color);
  font-weight: bold;
}

.exp-values {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
}

.exp-value-input {
  width: 200px !important;
  text-align: right;
  padding: 0.3rem 0.5rem !important;
  font-size: 0.9rem !important;
}

.total-exp {
  color: var(--text-color);
}

.error-message {
  color: var(--accent-color);
  font-size: 0.9rem;
  margin-top: 0.5rem;
}

.button-group {
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
}

.calculate-button,
.reset-button {
  padding: 0.8rem 2rem;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  cursor: pointer;
  transition: all var(--transition-speed) ease;
  flex: 1;
}

.calculate-button {
  background-color: var(--nav-bg);
  color: white;
}

.calculate-button:hover:not(.disabled) {
  background-color: var(--nav-hover);
}

.reset-button {
  background-color: #e74c3c;
  color: white;
}

.reset-button:hover:not(.disabled) {
  background-color: #c0392b;
}

.disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background-color: #ccc !important;
}

.disabled:hover {
  background-color: #ccc !important;
}

.result-section {
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: var(--card-shadow);
  margin-top: 2rem;
}

.result-section h2 {
  font-size: 1.2rem;
  color: var(--secondary-color);
  margin-bottom: 1rem;
}

.result-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.required-items h3 {
  font-size: 1.1rem;
  color: var(--primary-color);
  margin-bottom: 1rem;
}

.item-list {
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  margin-bottom: 1.5rem;
}

.item-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.8rem;
  background-color: #f8f9fa;
  border-radius: 6px;
}

.item-info {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.item-image {
  width: 32px;
  height: 32px;
  object-fit: contain;
}

.item-name {
  color: var(--text-color);
  font-size: 0.9rem;
}

.item-count {
  color: var(--primary-color);
  font-weight: bold;
  font-size: 1rem;
}

.reset-level-button {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all var(--transition-speed) ease;
  background-color: #e74c3c;
  color: white;
  margin-top: 0;
  margin-left: auto;
}

.reset-level-button:hover {
  background-color: #c0392b;
}

.burning-buttons {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
  align-items: center;
  justify-content: space-between;
}

.burning-left {
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 14px;
}

.burning-controls {
  display: flex;
  gap: 0.5rem;
}

.burning-button {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all var(--transition-speed) ease;
  background-color: #f8f9fa;
  color: var(--text-color);
}

.burning-button.active {
  background-color: var(--nav-bg);
  color: #fff;
}

.burning-button:hover:not(.active) {
  background-color: #e9ecef;
}

.burning-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background-color: #ccc !important;
  color: #666 !important;
}

.burning-button:disabled:hover {
  background-color: #ccc !important;
  color: #666 !important;
}

.burning-notice {
  color: var(--text-color);
  font-size: 0.8rem;
  opacity: 0.7;
  white-space: nowrap;
}

.burning-right {
  display: flex;
  align-items: center;
}

@media (max-width: 768px) {
  .back-calculator {
    padding: 1rem;
  }

  .input-section {
    padding: 1.5rem;
  }

  .button-group {
    flex-direction: column;
  }

  .calculate-button,
  .reset-button {
    width: 100%;
  }

  .exp-value-input {
    width: 150px !important;
  }

  .level-input-container {
    flex-direction: column;
    gap: 0.5rem;
  }

  .level-input,
  .percentage-input {
    width: 100%;
  }

  .result-section {
    padding: 1.5rem;
  }

  .burning-buttons {
    flex-direction: column;
    gap: 0.5rem;
    justify-content: flex-start;
    align-items: stretch;
  }
  
  .burning-button {
    width: 100%;
    margin-right: 0;
  }
}
</style>
